<template>
  <div ref="form" class="py-8">
    <h3 class="body-1-bold">{{ i18n(translations.title) }}</h3>
    <RevForm
      :id="formId"
      data-test="formCounterOfferReasons"
      :initial-values
      :validate
      @submit="handleSubmit"
      @visible-errors-change="scrollToErrorMessage"
    >
      <template #default="{ values, errors }">
        <div v-for="reasonsGroup in reasonsWithIcons" :key="reasonsGroup.id">
          <div class="mb-8 mt-32 flex items-center">
            <component :is="reasonsGroup.icon" />
            <p class="body-1-bold ml-8">
              {{ reasonsGroup.label }}
            </p>
          </div>

          <RevCheckbox
            v-for="(reason, index) in reasonsGroup.reasons"
            :id="reason.id"
            :key="reason.id"
            v-model="values.reasons"
            class="mt-16"
            :data-qa="`${reasonsGroup.id}-option-${index}`"
            :value="reason.id"
          >
            <template #label>
              {{ reason.label }}
            </template>
          </RevCheckbox>
        </div>

        <p v-if="errors.reasons" class="text-static-danger-hi body-2">
          {{ errors.reasons }}
        </p>
      </template>
    </RevForm>
    <RevInfoBlock
      class="mt-32"
      :icon="iconBattery"
      :title="i18n(translations.infoBlock)"
      variant="default"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconEyeOpen } from '@ds/icons/IconEyeOpen'
import { IconHeartDiagnostic } from '@ds/icons/IconHeartDiagnostic'

import type { CounterOfferFormattedReasonsGroup } from '~/scopes/buyback/pages/orderDetails/components/OrderOnHoldModal/counterOfferReasonsGroupsAdapter'

import { ON_HOLD_STEPS } from '../../constants'
import { filterPreselectedReasons } from '../helpers'

import translations from './CounterOfferReasonsView.translations'

const props = defineProps<{
  reasonsGroups: CounterOfferFormattedReasonsGroup[]
}>()

const emit = defineEmits<{
  (e: 'next', payload: { step: string; payload: { reasons: string[] } }): void
}>()

const i18n = useI18n()

const form = ref<HTMLElement | null>(null)

const initialValues = computed(() => ({
  reasons: filterPreselectedReasons(props.reasonsGroups),
}))

const formId = ON_HOLD_STEPS.MAKE_COUNTER_OFFER_REASONS
const iconBattery = IconBattery

const validate = computed(() => {
  const requiredMessage = i18n(translations.requiredErrorMessage)

  return makeValidate<typeof initialValues.value>({
    reasons: [required(requiredMessage)],
  })
})

const groupIcons = [IconEyeOpen, IconHeartDiagnostic]

const reasonsWithIcons = computed(() =>
  props.reasonsGroups.map((reasonGroup, index) => ({
    ...reasonGroup,
    icon: groupIcons[index],
  })),
)

const handleSubmit = (values: { reasons: string[] }) => {
  emit('next', {
    step: ON_HOLD_STEPS.MAKE_COUNTER_OFFER_DETAILS,
    payload: values,
  })
}

const scrollToErrorMessage = () => {
  form.value?.scrollIntoView(false)
}
</script>
